import { createFileRoute, Link } from "@tanstack/react-router";

import { SiteLayout } from "@/components/SiteLayout";

const TITLE = "Como funciona o TrajetoFácil — do endereço ao custo da viagem";
const DESCRIPTION =
  "Passo a passo do TrajetoFácil: como informar origem, destino e paradas, escolher o veículo, preencher combustível e interpretar cada número do resultado da rota.";
const URL = "https://www.trajetofacil.com.br/como-funciona";

const STEPS = [
  {
    title: "1. Informe origem e destino",
    body: "Digite CEP, cidade ou endereço completo em cada campo. Conforme você escreve, aparecem sugestões de locais; escolher uma sugestão garante coordenadas precisas e um traçado de rota mais fiel.",
  },
  {
    title: "2. Adicione paradas, se houver",
    body: "É possível incluir até seis pontos. Cada parada vira um trecho independente, com distância, combustível e praças de pedágio próprios — a ordem em que você adiciona é a ordem em que a rota é calculada.",
  },
  {
    title: "3. Escolha o veículo",
    body: "Carro, moto, ônibus ou caminhão. Para ônibus e caminhão, informe o número de eixos: é ele que define a tarifa cobrada em cada praça.",
  },
  {
    title: "4. Preencha combustível ou valor por km",
    body: "Informe o preço do litro e o consumo em km/l para o cálculo detalhado. Se preferir trabalhar com um valor fechado por quilômetro, deixe esses dois campos em branco e preencha só o valor por km.",
  },
  {
    title: "5. Marque ida e volta quando for o caso",
    body: "Com a opção ativada, os dois sentidos são calculados separadamente — porque nem sempre a volta percorre o mesmo caminho nem cruza as mesmas praças.",
  },
  {
    title: "6. Busque a rota e leia o resultado",
    body: "O mapa mostra o traçado e os pontos; abaixo aparecem os cards com distância, tempo, combustível e pedágio de cada sentido, e o card verde com o custo total da viagem.",
  },
];

const RESULT_FIELDS = [
  {
    label: "Distância",
    body: "Quilômetros do trajeto calculado, por sentido. É a base de todo o cálculo de combustível.",
  },
  {
    label: "Tempo estimado",
    body: "Duração prevista em condições normais de tráfego. Não inclui paradas, refeições nem congestionamentos pontuais.",
  },
  {
    label: "Combustível",
    body: "Distância dividida pelo consumo informado, multiplicada pelo preço do litro. Calculado separadamente para cada trecho.",
  },
  {
    label: "Pedágio",
    body: "Soma das tarifas das praças identificadas ao longo do trajeto, já na categoria do veículo escolhido. Você vê o detalhe praça por praça.",
  },
  {
    label: "Custo total",
    body: "Combustível mais pedágio de todos os trechos. É o custo variável do deslocamento completo.",
  },
  {
    label: "Valor a repassar",
    body: "Aparece quando você usa o multiplicador: o custo com a margem aplicada, útil para cobrar de um cliente ou ratear entre passageiros.",
  },
];

export const Route = createFileRoute("/como-funciona")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: HowItWorksPage,
});

function HowItWorksPage() {
  return (
    <SiteLayout>
      <article className="space-y-10 text-base leading-7 text-foreground">
        <header>
          <h1 className="text-3xl font-extrabold tracking-tight">Como funciona o TrajetoFácil</h1>
          <p className="mt-3 text-muted-foreground">
            Do endereço digitado ao custo final da viagem: veja o que acontece em cada etapa e o que
            significa cada número apresentado no resultado.
          </p>
        </header>

        <section className="space-y-4">
          <h2 className="text-xl font-bold">O caminho de uma simulação</h2>
          <ol className="space-y-4">
            {STEPS.map((step) => (
              <li key={step.title} className="rounded-2xl border border-border bg-card p-5">
                <h3 className="font-bold">{step.title}</h3>
                <p className="mt-1.5 text-sm leading-6 text-muted-foreground">{step.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">O que acontece por trás do cálculo</h2>
          <p>
            Ao buscar a rota, o endereço é convertido em coordenadas, o traçado entre os pontos é gerado e
            essa geometria é cruzada com a base de praças de pedágio. Cada praça encontrada no caminho
            entra com a tarifa da categoria escolhida, respeitando o sentido do trajeto. O combustível é
            calculado sobre a distância de cada trecho.
          </p>
          <p>
            Consultas iguais feitas em sequência reaproveitam o resultado recente, o que deixa a resposta
            mais rápida sem alterar os valores. O detalhamento completo do método está na página de{" "}
            <Link to="/metodologia" className="font-medium text-primary underline underline-offset-4">
              metodologia
            </Link>
            .
          </p>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-bold">Como ler o resultado</h2>
          <dl className="space-y-3">
            {RESULT_FIELDS.map((f) => (
              <div key={f.label} className="rounded-xl border border-border bg-card p-4">
                <dt className="font-semibold">{f.label}</dt>
                <dd className="mt-1 text-sm leading-6 text-muted-foreground">{f.body}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-bold">Depois de calcular</h2>
          <p>
            Você pode compartilhar a simulação por link — quem abrir vê a mesma rota, com os mesmos
            parâmetros — ou ajustar um campo e recalcular para comparar cenários. Para entender cada
            recurso em detalhe, veja a página de{" "}
            <Link to="/recursos" className="font-medium text-primary underline underline-offset-4">
              recursos
            </Link>{" "}
            e os{" "}
            <Link to="/casos-de-uso" className="font-medium text-primary underline underline-offset-4">
              casos de uso
            </Link>
            .
          </p>
          <p>
            <Link to="/" className="font-medium text-primary underline underline-offset-4">
              Abrir a calculadora
            </Link>
          </p>
        </section>
      </article>
    </SiteLayout>
  );
}
